import { type KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Person } from "./api.ts";
import { FIND_PAST, faceNames, finds, inOrder } from "./faces.ts";
import { Avatar, Icon } from "./souvenir.ts";
import { ItemIcon } from "./ui.tsx";

// Whose wants People's Together compares, picked from a row of faces (features/people.md, "Whose list,
// picked by face" and "Together"): each person's face with their first name under it, You first and then
// everyone by first name; those picked ringed in their colour, their names in bold. Each tap picks or
// leaves out one. The row scrolls sideways when it doesn't fit (swipe, trackpad, a mouse's wheel, and on a
// computer the arrow keys, Home and End, which move along it, Space or Enter picking), its edge fading on
// the side with more, and the first one picked kept in view. Past FIND_PAST people, "Find someone" starts
// it.

// How far in from an edge the fade reaches: a face is in view once it's clear of it.
const FADE = 32;
// A face's width, and its picture's, as the row starts with (app.css, .tm-whose), and how far the row
// runs past the column on either side.
const FACE = 72;
const AVATAR = 48;
const BLEED = 16;

export function Whose({
  members,
  you,
  picked,
  onToggle,
}: {
  members: Person[];
  you: string;
  // Whose wants are compared.
  picked: readonly string[];
  // Picks someone, or leaves them out (`on`: whether they're picked after).
  onToggle: (id: string, on: boolean) => void;
}) {
  const [query, setQuery] = useState("");
  const row = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const people = inOrder(members, you);
  const names = faceNames(members, you);
  const find = members.length > FIND_PAST;
  const shown = find ? people.filter((p) => finds(p, query, you)) : people;
  const searching = find && query.trim() !== "";
  // The face Tab reaches: the one last moved to, else the first picked, else the first face.
  const [moved, setMoved] = useState<string | null>(null);
  const firstPicked = shown.find((p) => picked.includes(p.id))?.id;
  const focusable = shown.some((p) => p.id === moved) ? moved : (firstPicked ?? shown[0]?.id);
  // What's revealed: the first picked as the page opens, then whoever was last tapped or moved to.
  const value = moved ?? firstPicked ?? shown[0]?.id ?? "";

  // Which edges have more beyond them, for the fade. And when the row doesn't fit, its faces a little
  // narrower or wider so that the last one in view is cut through its picture's middle at the edge, at
  // any width: a face only just showing, or none, wouldn't say the row goes on.
  const [more, setMore] = useState({ start: false, end: false });
  const measure = () => {
    const el = row.current;
    if (!el) return;
    const width = el.clientWidth;
    const count = el.children.length;
    const fits = BLEED + (count - 1) * FACE + AVATAR + (FACE - AVATAR) / 2 <= width;
    const room = width - BLEED - AVATAR / 2;
    const face = fits ? FACE : room / Math.max(1, Math.round(room / FACE));
    if (el.style.getPropertyValue("--tm-face") !== `${face}px`) el.style.setProperty("--tm-face", `${face}px`);
    const start = el.scrollLeft > 1;
    const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setMore((m) => (m.start === start && m.end === end ? m : { start, end }));
  };
  useEffect(() => {
    const el = row.current;
    if (!el) return;
    const resized = new ResizeObserver(measure);
    resized.observe(el);
    return () => resized.disconnect();
  });

  // The one shown, in view: in the middle as the page opens (or reopens, on someone else), and just far
  // enough in, clear of the fade, as it changes.
  const reveal = (id: string, how: "middle" | "near", smooth: boolean) => {
    const el = row.current;
    const face = el?.querySelector<HTMLElement>(`[data-person="${CSS.escape(id)}"]`);
    if (!el || !face) return;
    const box = el.getBoundingClientRect();
    const f = face.getBoundingClientRect();
    let left = el.scrollLeft;
    if (how === "middle") left += f.left + f.width / 2 - (box.left + box.width / 2);
    else if (f.left < box.left + FADE) left -= box.left + FADE - f.left;
    else if (f.right > box.right - FADE) left += f.right - (box.right - FADE);
    else return;
    // Gliding a step along; a jump of more than the row's width (End, Home) goes straight there.
    const glide = smooth && Math.abs(left - el.scrollLeft) <= el.clientWidth;
    const calm = matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ left, behavior: glide && !calm ? "smooth" : "auto" });
  };
  // After each change: on opening, the first picked in the middle; a search narrowing the row, from its
  // start (cleared, the row comes back with the last one tapped in view); a face tapped or moved to, in
  // view.
  const before = useRef<{ value: string; query: string } | null>(null);
  // Whose face changed last, as it's tapped: brought clear of the fade.
  const touched = useRef<string | null>(null);
  useLayoutEffect(() => {
    const was = before.current;
    before.current = { value, query };
    measure();
    if (!was) reveal(value, "middle", false);
    else if (was.query !== query) {
      if (row.current) row.current.scrollLeft = 0;
      if (!searching) reveal(value, "near", false);
    } else if (was.value !== value) reveal(value, "near", true);
    else if (touched.current) reveal(touched.current, "near", true);
    touched.current = null;
    measure();
  });

  // A mouse's wheel, turning over the row, moves it sideways while it has further to go; then the page
  // scrolls on as usual. A trackpad's sideways swipe scrolls it by itself.
  useEffect(() => {
    const el = row.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      if (e.shiftKey || e.ctrlKey || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const room = e.deltaY > 0 ? el.scrollWidth - el.clientWidth - el.scrollLeft : el.scrollLeft;
      if (room <= 1) return;
      e.preventDefault();
      el.scrollLeft += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, []);

  const toggle = (id: string) => {
    setMoved(id);
    touched.current = id;
    onToggle(id, !picked.includes(id));
  };
  const moveTo = (id: string) => {
    setMoved(id);
    row.current?.querySelector<HTMLElement>(`[data-person="${CSS.escape(id)}"]`)?.focus({ preventScroll: true });
  };
  // On a computer the arrow keys move along the row, as in a toolbar, and Home and End go to its ends;
  // Space or Enter picks the one reached, or leaves it out.
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const at = shown.findIndex((p) => p.id === (e.target as HTMLElement).dataset["person"]);
    if (at < 0) return;
    const to =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? Math.min(at + 1, shown.length - 1)
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? Math.max(at - 1, 0)
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? shown.length - 1
              : null;
    if (to === null) return;
    e.preventDefault();
    const next = shown[to];
    if (next) moveTo(next.id);
  };

  return (
    <div className="tm-whose">
      {find && (
        <search className="tm-search tm-whose-find">
          <ItemIcon name="search" size={20} />
          <input
            ref={input}
            type="search"
            className="body"
            enterKeyHint="go"
            autoComplete="off"
            spellCheck={false}
            placeholder="Find someone"
            aria-label="Find someone"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && query !== "") {
                e.preventDefault();
                e.stopPropagation();
                setQuery("");
              } else if (e.key === "Enter" && searching && shown.length === 1 && shown[0]) {
                e.preventDefault();
                setMoved(shown[0].id);
                if (!picked.includes(shown[0].id)) onToggle(shown[0].id, true);
              }
            }}
          />
          {query !== "" && (
            <button
              type="button"
              className="tm-search-clear"
              aria-label="Clear the search"
              onClick={() => {
                setQuery("");
                input.current?.focus();
              }}
            >
              <Icon name="close" size={16} />
            </button>
          )}
        </search>
      )}
      {find && (
        <span className="tm-hidden" aria-live="polite">
          {searching ? `${shown.length} ${shown.length === 1 ? "person" : "people"}` : ""}
        </span>
      )}
      {/* biome-ignore lint/a11y/useSemanticElements: a row that scrolls sideways; a fieldset takes its content's
      width as its least, so it would never scroll */}
      <div
        ref={row}
        role="group"
        aria-label="Whose wants"
        className="tm-whose-row"
        data-more-start={more.start || undefined}
        data-more-end={more.end || undefined}
        onScroll={measure}
        onKeyDown={onKey}
        hidden={shown.length === 0}
      >
        {shown.map((p) => {
          const on = picked.includes(p.id);
          return (
            <button
              key={p.id}
              type="button"
              aria-pressed={on}
              aria-label={p.id === you ? "You" : p.name}
              title={p.id === you ? undefined : p.name}
              tabIndex={p.id === focusable ? 0 : -1}
              data-person={p.id}
              className="tm-face"
              onClick={() => toggle(p.id)}
            >
              <Avatar name={p.name} color={p.color} {...(p.src ? { src: p.src } : {})} size={48} />
              <span className="tm-face-name caption">{names.get(p.id)}</span>
            </button>
          );
        })}
      </div>
      {shown.length === 0 && (
        <p className="callout tm-muted tm-whose-none">No one in the trip is called “{query.trim()}”.</p>
      )}
    </div>
  );
}
